// ✅ Trekkin.app — MiniMapRealTimeStatic.jsx v4.3.1
// 🔹 Base = v4.3.0 (NADA removido, só afinado)
// 🔹 iOS / Web: minimapa quase igual ao MapView (sem suavização extra)
// 🔹 Android: mantém anti-tremor (clean + smoothPoints janela 2)
// 🔹 Nenhuma função removida

import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "maplibre-gl/dist/maplibre-gl.css";
import { usePanelsStore } from "../stores/usePanelsStore";
import { useAuthStore } from "../stores/useAuthStore";
import { useCan } from "../hooks/useCan";
import { createMapLibreLayer } from "../utils/leafletMaplibreLayer";
import {
  buildLiteStyle,
  buildProStyle,
  ensurePmtilesProtocol,
  readMapStylePref,
  resolveMapStyle,
} from "../utils/mapStyle";

// Plataforma aproximada (para suavização só em Android)
const isAndroidUA =
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent || "");

// -------------------------------------------------------------
// ÍCONE GUIA VERDE (MESMO DO MAPVIEW)
// -------------------------------------------------------------
const trekkinIcon = L.divIcon({
  className: "",
  html: `
    <div style="
      width:16px;
      height:16px;
      background:#28a745;
      border-radius:50%;
      border:2px solid white;
      box-shadow:0 0 6px rgba(0,0,0,0.3);
    "></div>
  `,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// -------------------------------------------------------------
// 🔥 FILTRO ANTI-GATAFUNHO (~<1.5m) + (suavização opcional Android)
// -------------------------------------------------------------
function cleanPathForMiniMap(points) {
  if (!Array.isArray(points) || points.length < 3) return points;

  const cleaned = [];
  let last = points[0];
  cleaned.push(last);

  for (let i = 1; i < points.length; i++) {
    const p = points[i];

    // distância aproximada em graus (suficiente para jitter pequeno)
    const dLat = p.lat - last.lat;
    const dLon = p.lon - last.lon;
    const dist = Math.sqrt(dLat * dLat + dLon * dLon);

    // ignora movimentos muito pequenos (~ < 1.5m)
    if (dist < 0.000015) continue;

    cleaned.push(p);
    last = p;
  }

  return cleaned;
}

// Suavização leve por janela móvel (mantida para Android)
function smoothPoints(pts, size = 2) {
  if (!Array.isArray(pts) || pts.length <= 2) return pts;

  const res = [];
  for (let i = 0; i < pts.length; i++) {
    const start = Math.max(0, i - size);
    const end = Math.min(pts.length - 1, i + size);
    const sub = pts.slice(start, end + 1);

    const avgLat = sub.reduce((s, p) => s + p.lat, 0) / sub.length;
    const avgLon = sub.reduce((s, p) => s + p.lon, 0) / sub.length;

    res.push({ lat: avgLat, lon: avgLon });
  }
  return res;
}

export default function MiniMapRealTimeStatic({
  points = [],
  trailBase = [],
  height = 160,
  forceLite = false,
  liteRasterFallback = true,
}) {
  const { userPosition } = usePanelsStore();
  const user = useAuthStore((s) => s.user);
  const isAdminUser = String(user?.role || "").toLowerCase() === "admin";
  const canUseLite = useCan("PanelMap", "use_lite");
  const canUsePro = useCan("PanelMap", "use_pro");
  const mapStylePref = useMemo(() => readMapStylePref(user), [user?.mapStyle]);
  const resolvedMapStyle = useMemo(() => {
    if (forceLite) return "lite";
    const pref = String(mapStylePref || "").toLowerCase();
    if (pref === "openstreet" && isAdminUser) return "openstreet";
    return resolveMapStyle(pref, canUseLite, canUsePro);
  }, [forceLite, mapStylePref, canUseLite, canUsePro, isAdminUser]);

  const mapRef = useRef(null);
  const baseRef = useRef(null);
  const liveRef = useRef(null);
  const userRef = useRef(null);
  const baseTileRef = useRef(null);
  const baseLibreRef = useRef(null);
  const containerId = useRef(`mini-map-${Math.random().toString(36).slice(2)}`);

  const [initialized, setInitialized] = useState(false);
  const [firstFit, setFirstFit] = useState(false);
  const [hadRoute, setHadRoute] = useState(false);

  const isShareMode =
    typeof window !== "undefined" &&
    window.location.search.includes("share=");

  useEffect(() => {
    const hasRoute =
      (Array.isArray(points) && points.length > 1) ||
      (Array.isArray(trailBase) && trailBase.length > 1);
    if (hasRoute && !hadRoute) {
      setFirstFit(false);
      setHadRoute(true);
    } else if (!hasRoute && hadRoute) {
      setHadRoute(false);
    }
  }, [points, trailBase, hadRoute]);

  // -------------------------------------------------------------
  // INIT MAP
  // -------------------------------------------------------------
  useEffect(() => {
    if (mapRef.current) return;

    const el = document.getElementById(containerId.current);
    if (!el) return;

    const map = L.map(el, {
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
      minZoom: 5,
      maxZoom: 19,
      worldCopyJump: true,
      zoomSnap: 0.25,
    });

    baseRef.current = L.polyline([], {
      color: "#007bff",
      weight: 3,
      opacity: 0.9,
    }).addTo(map);

    liveRef.current = L.polyline([], {
      color: "#ff4b4b",
      weight: 3,
      opacity: 0.9,
    }).addTo(map);

    userRef.current = L.marker([0, 0], {
      icon: trekkinIcon,
      zIndexOffset: 1000,
    }).addTo(map);

    mapRef.current = map;
    setInitialized(true);
    window.setTimeout(() => {
      try {
        map.invalidateSize();
      } catch {}
    }, 60);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (baseTileRef.current) {
      map.removeLayer(baseTileRef.current);
      baseTileRef.current = null;
    }
    if (baseLibreRef.current) {
      map.removeLayer(baseLibreRef.current);
      baseLibreRef.current = null;
    }

    if (liteRasterFallback || resolvedMapStyle === "openstreet") {
      baseTileRef.current = L.tileLayer(
        "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          tileSize: 256,
          maxZoom: 19,
          minZoom: 5,
          crossOrigin: true,
          reuseTiles: true,
          keepBuffer: 2,
        }
      ).addTo(map);
    } else {
      ensurePmtilesProtocol();
      const style =
        resolvedMapStyle === "pro" ? buildProStyle() : buildLiteStyle();
      baseLibreRef.current = createMapLibreLayer({ style }).addTo(map);
    }
    window.setTimeout(() => {
      try {
        map.invalidateSize();
      } catch {}
    }, 60);
  }, [resolvedMapStyle, liteRasterFallback]);

  // -------------------------------------------------------------
  // Atualiza GUIA
  // -------------------------------------------------------------
  useEffect(() => {
    if (!initialized || !userPosition || isShareMode) return;
    const hasRoute =
      (Array.isArray(points) && points.length > 1) ||
      (Array.isArray(trailBase) && trailBase.length > 1);
    if (hasRoute) return;

    const { lat, lon } = userPosition;
    if (!lat || !lon) return;

    userRef.current.setLatLng([lat, lon]);

    if (!firstFit) {
      mapRef.current.setView([lat, lon], 15, { animate: false });
      setFirstFit(true);
      setTimeout(() => mapRef.current.invalidateSize(true), 80);
    }
  }, [userPosition, initialized, firstFit, isShareMode, points, trailBase]);

  // -------------------------------------------------------------
  // Trilho base (GPX)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!initialized || !baseRef.current) return;

    if (!trailBase?.length) {
      baseRef.current.setLatLngs([]);
      return;
    }

    const cleaned = cleanPathForMiniMap(trailBase);
    const processed = isAndroidUA ? smoothPoints(cleaned, 2) : cleaned;
    const latlngs = processed.map((p) => [p.lat, p.lon]);

    baseRef.current.setLatLngs(latlngs);

    if (!firstFit && latlngs.length > 1 && !isShareMode) {
      mapRef.current.fitBounds(L.polyline(latlngs).getBounds(), {
        padding: [10, 10],
      });
      setFirstFit(true);
      setTimeout(() => mapRef.current.invalidateSize(true), 120);
    }
  }, [trailBase, firstFit, initialized, isShareMode]);

  // -------------------------------------------------------------
  // Rasto Live
  // -------------------------------------------------------------
  useEffect(() => {
    if (!initialized || !liveRef.current) return;

    // 🔥 iOS/Web: só clean; Android: clean + smooth
    const cleaned = cleanPathForMiniMap(points);
    const processed = isAndroidUA ? smoothPoints(cleaned, 2) : cleaned;

    const latlngs = processed.map((p) => [p.lat, p.lon]);
    liveRef.current.setLatLngs(latlngs);

    if (!isShareMode) {
      if (!firstFit && latlngs.length > 1) {
        mapRef.current.fitBounds(L.polyline(latlngs).getBounds(), {
          padding: [10, 10],
        });
        setFirstFit(true);
        setTimeout(() => mapRef.current.invalidateSize(true), 120);
        return;
      }
      if (firstFit && latlngs.length) {
        setTimeout(() => mapRef.current.invalidateSize(true), 120);
      }
      return;
    }

    // Modo Viewer segue último ponto
    if (!latlngs.length) return;
    const last = latlngs[latlngs.length - 1];

    userRef.current.setLatLng(last);

    if (!firstFit) {
      mapRef.current.setView(last, 15, { animate: false });
      setFirstFit(true);
    } else {
      mapRef.current.panTo(last, { animate: true, duration: 0.4 });
    }
  }, [points, firstFit, initialized, isShareMode]);

  return (
    <div
      id={containerId.current}
      style={{
        width: "100%",
        height,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        background: "#e9f1eb",
      }}
    />
  );
}
